import { useState } from 'react';

// Only show editing tools on the Readdy editor, not on the live site
const isEditMode = typeof window !== 'undefined' &&
  (window.location.hostname.includes('readdy.ai') ||
   window.location.hostname === 'localhost' ||
   window.location.hostname === '127.0.0.1');

interface CustomArtwork {
  id: number;
  title: string;
  artist: string;
  year: string;
  medium: string;
  dimensions: string;
  imageUrl: string;
  description: string;
  location: string;
  sort_order: number;
}

type WingKey = 'main' | 'north' | 'east' | 'south' | 'west';

interface ArtworkGalleryProps {
  lightIntensity: number;
  selectedArtwork: number | null;
  onSelectArtwork: (id: number | null) => void;
  artworks: CustomArtwork[];
  activeWing: WingKey | null;
  onAddArtwork: () => void;
  onNavigateToWing: (wing: WingKey | null) => void;
  onDeleteArtwork?: (id: number) => void;
  onEditArtwork?: (artwork: CustomArtwork) => void;
}

// ─── Edit Modal ───────────────────────────────────────────────────────────────
function EditArtworkModal({
  artwork,
  onSave,
  onClose,
}: {
  artwork: CustomArtwork;
  onSave: (updated: CustomArtwork) => void;
  onClose: () => void;
}) {
  const [form, setForm] = useState<CustomArtwork>({ ...artwork });
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saveSuccess, setSaveSuccess] = useState(false);

  const wingOptions = [
    { value: 'Main Gallery', label: 'Main Gallery' },
    { value: 'North Wing',   label: 'Gallery Room 1 (North)' },
    { value: 'East Wing',    label: 'Gallery Room 2 (East)' },
    { value: 'South Wing',   label: 'Gallery Room 3 (South)' },
    { value: 'West Wing',    label: 'Gallery Room 4 (West)' },
  ];

  const handleChange = (field: keyof CustomArtwork, value: string) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    setSaveError(null);
    setSaveSuccess(false);
    try {
      await onSave(form);
      setSaveSuccess(true);
      setTimeout(() => {
        onClose();
      }, 800);
    } catch (err: any) {
      setSaveError('Save failed. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-black/8 px-6 py-4 flex items-center justify-between z-10 rounded-t-xl">
          <div>
            <h2 className="text-base font-medium text-black" style={{ fontFamily: "'Inter', sans-serif" }}>Edit Artwork</h2>
            <p className="text-xs text-black/40 mt-0.5">Changes are saved to your gallery</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-black/5 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-lg text-black/50"></i>
          </button>
        </div>

        {/* Thumbnail */}
        <div className="px-6 pt-5 pb-2">
          <div className="w-full h-40 bg-stone-50 rounded-lg overflow-hidden flex items-center justify-center border border-black/6">
            <img
              src={form.imageUrl}
              alt={form.title}
              className="max-w-full max-h-full object-contain"
            />
          </div>
        </div>

        {/* Fields */}
        <div className="px-6 pb-6 space-y-4 mt-2">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-black/60 mb-1.5 uppercase tracking-wide">Title</label>
            <input
              type="text"
              value={form.title}
              onChange={e => handleChange('title', e.target.value)}
              className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
              placeholder="Artwork title"
            />
          </div>

          {/* Artist */}
          <div>
            <label className="block text-xs font-medium text-black/60 mb-1.5 uppercase tracking-wide">Artist</label>
            <input
              type="text"
              value={form.artist}
              onChange={e => handleChange('artist', e.target.value)}
              className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
              placeholder="Artist name"
            />
          </div>

          {/* Year + Medium */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-black/60 mb-1.5 uppercase tracking-wide">Year</label>
              <input
                type="text"
                value={form.year}
                onChange={e => handleChange('year', e.target.value)}
                className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
                placeholder="e.g. 2023"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-black/60 mb-1.5 uppercase tracking-wide">Medium</label>
              <input
                type="text"
                value={form.medium}
                onChange={e => handleChange('medium', e.target.value)}
                className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
                placeholder="e.g. Oil on canvas"
              />
            </div>
          </div>

          {/* Dimensions */}
          <div>
            <label className="block text-xs font-medium text-black/60 mb-1.5 uppercase tracking-wide">Dimensions</label>
            <input
              type="text"
              value={form.dimensions}
              onChange={e => handleChange('dimensions', e.target.value)}
              className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors"
              placeholder="e.g. 60 × 80 cm"
            />
          </div>

          {/* Location / Wing */}
          <div>
            <label className="block text-xs font-medium text-black/60 mb-1.5 uppercase tracking-wide">Gallery Room</label>
            <select
              value={form.location}
              onChange={e => handleChange('location', e.target.value)}
              className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors bg-white cursor-pointer"
            >
              {wingOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-black/60 mb-1.5 uppercase tracking-wide">Description</label>
            <textarea
              value={form.description}
              onChange={e => handleChange('description', e.target.value)}
              rows={4}
              maxLength={500}
              className="w-full border border-black/12 rounded-lg px-3 py-2 text-sm text-black focus:outline-none focus:border-black/30 transition-colors resize-none"
              placeholder="Describe this artwork..."
            />
            <p className="text-xs text-black/30 mt-1 text-right">{form.description.length}/500</p>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-black/12 rounded-lg text-sm text-black/60 hover:bg-black/4 transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex-1 py-2.5 bg-black text-white rounded-lg text-sm font-medium hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              {saving ? 'Saving…' : saveSuccess ? '✓ Saved!' : 'Save Changes'}
            </button>
          </div>

          {saveError && (
            <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
              <i className="ri-error-warning-line text-red-500 text-sm"></i>
              <p className="text-xs text-red-600">{saveError}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Artwork Card ─────────────────────────────────────────────────────────────
interface ArtworkCardProps {
  artwork: CustomArtwork;
  onClick: () => void;
  onDelete?: (id: number) => void;
  onEdit?: (artwork: CustomArtwork) => void;
}

function ArtworkCard({ artwork, onClick, onDelete, onEdit }: ArtworkCardProps) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDelete(true);
  };

  const handleConfirm = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDelete?.(artwork.id);
    setConfirmDelete(false);
  };

  const handleCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setConfirmDelete(false);
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit?.(artwork);
  };

  return (
    <div
      className="relative group cursor-pointer"
      onMouseLeave={() => setConfirmDelete(false)}
      onClick={onClick}
    >
      <div className="relative bg-white overflow-hidden shadow-sm group-hover:shadow-lg transition-all flex items-center justify-center" style={{ minHeight: 120 }}>
        <img
          src={artwork.imageUrl}
          alt={artwork.title}
          className="w-full h-auto object-contain block"
          style={{ maxHeight: 260 }}
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>

        {/* Editor-only action buttons */}
        {isEditMode && (
          <>
            {/* Edit button — top-right */}
            {onEdit && (
              <div className="absolute top-2 right-2 z-10">
                <button
                  onClick={handleEditClick}
                  className="w-8 h-8 flex items-center justify-center bg-white hover:bg-stone-100 text-black/70 rounded-full shadow-md border border-black/10 transition-colors cursor-pointer whitespace-nowrap"
                  title="Edit artwork details"
                >
                  <i className="ri-edit-line text-sm"></i>
                </button>
              </div>
            )}

            {/* Delete button — bottom-left */}
            {onDelete && (
              <div className="absolute bottom-2 left-2 z-10" onClick={e => e.stopPropagation()}>
                {!confirmDelete ? (
                  <button
                    onClick={handleDeleteClick}
                    className="w-8 h-8 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded-full shadow-md transition-colors cursor-pointer whitespace-nowrap"
                    title="Delete artwork"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                ) : (
                  <div className="flex items-center gap-1 bg-white border border-red-200 rounded-lg shadow-lg px-2 py-1">
                    <span className="text-xs text-red-600 font-medium whitespace-nowrap">Delete?</span>
                    <button
                      onClick={handleConfirm}
                      className="w-6 h-6 flex items-center justify-center bg-red-500 hover:bg-red-600 text-white rounded text-xs cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-check-line"></i>
                    </button>
                    <button
                      onClick={handleCancel}
                      className="w-6 h-6 flex items-center justify-center bg-stone-200 hover:bg-stone-300 text-stone-700 rounded text-xs cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <div className="mt-3 text-left">
        <h3 className="text-sm font-light text-black whitespace-nowrap overflow-hidden text-ellipsis">{artwork.title}</h3>
        <p className="text-xs text-black/50 mt-1 whitespace-nowrap">{artwork.year}</p>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ArtworkGallery({
  lightIntensity,
  selectedArtwork,
  onSelectArtwork,
  artworks,
  activeWing,
  onAddArtwork,
  onNavigateToWing,
  onDeleteArtwork,
  onEditArtwork,
}: ArtworkGalleryProps) {
  const [editingArtwork, setEditingArtwork] = useState<CustomArtwork | null>(null);

  const handleEditSave = async (updated: CustomArtwork) => {
    await onEditArtwork?.(updated);
    setEditingArtwork(null);
  };

  return (
    <div className="relative w-full min-h-screen bg-white">
      <div className="relative z-10 max-w-7xl mx-auto px-8 py-16">
        {/* Header */}
        <div className="flex items-center justify-between mb-12 border-b border-black/10 pb-8">
          <div>
            <h1 className="text-4xl font-light text-black mb-2" style={{ fontFamily: "'Inter', sans-serif" }}>Gallery Overview</h1>
            <p className="text-sm text-black/50 font-light">Select an artwork to view details or enter the immersive gallery view</p>
          </div>
          {isEditMode && (
            <button
              onClick={onAddArtwork}
              className="flex items-center gap-2 px-6 py-3 bg-black text-white text-sm font-light rounded-lg hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base"></i>
              Add Artwork
            </button>
          )}
        </div>

        {/* Enter Gallery Button */}
        <div className="mb-16">
          <button
            onClick={() => onNavigateToWing('main')}
            className="w-full aspect-[21/9] bg-white border border-black/10 rounded overflow-hidden shadow-sm hover:shadow-lg transition-all cursor-pointer group relative"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-white to-black/5"></div>
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors"></div>
            <div className="relative h-full flex flex-col items-center justify-center gap-4">
              <i className="ri-door-open-line text-6xl text-black/30 group-hover:text-black/50 transition-colors"></i>
              <h3 className="text-2xl font-light text-black whitespace-nowrap" style={{ fontFamily: "'Inter', sans-serif" }}>Enter Immersive Gallery View</h3>
              <p className="text-sm text-black/50 whitespace-nowrap">360° navigation • Walk up to artworks • Zoom in</p>
            </div>
          </button>
        </div>

        {/* All Artworks Grid */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-light text-black" style={{ fontFamily: "'Inter', sans-serif" }}>All Artworks</h2>
            <span className="text-sm text-black/50">{artworks.length} piece{artworks.length !== 1 ? 's' : ''}</span>
          </div>

          {artworks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 border-2 border-dashed border-black/10 rounded-xl">
              <i className="ri-image-add-line text-5xl text-black/20 mb-4"></i>
              <p className="text-base text-black/40 font-light mb-2">No artworks yet</p>
              <p className="text-sm text-black/30 font-light mb-6">
                {isEditMode ? 'Click "Add Artwork" to upload your first piece' : 'Artworks coming soon'}
              </p>
              {isEditMode && (
                <button
                  onClick={onAddArtwork}
                  className="flex items-center gap-2 px-6 py-3 bg-black text-white text-sm font-light rounded-lg hover:bg-stone-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-add-line text-base"></i>
                  Add Artwork
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-5 gap-8">
              {artworks.map((artwork) => (
                <ArtworkCard
                  key={artwork.id}
                  artwork={artwork}
                  onClick={() => onSelectArtwork(artwork.id)}
                  onDelete={isEditMode ? onDeleteArtwork : undefined}
                  onEdit={isEditMode ? setEditingArtwork : undefined}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {editingArtwork && (
        <EditArtworkModal
          artwork={editingArtwork}
          onSave={handleEditSave}
          onClose={() => setEditingArtwork(null)}
        />
      )}
    </div>
  );
}
